import {
  ApiChartDisplayOptions,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiTableGrouping,
} from "@incendium/api";
import { TTableCustomCellRender } from "features/analytics";
import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import { useGroupMetricsByCategory } from "features/analytics/hooks/useGroupMetricsByCategory";
import { useExplorerContext } from "features/explorer/providers/ExplorerProvider";
import { useMemo } from "react";

interface IExplorerTableProps {
  onClick?: { [field: string]: (v: string, o?: string | undefined) => void };
  metrics: ApiMetric[];
  limit?: number;
  colWidths?: { [key: string]: number };
  extraDimensions?: ApiDimension[];
  dontIncludeEmpty?: boolean;
  tableCustomHeaderRender?: TTableCustomCellRender;
  ignoreAttrModel?: boolean;
  groupedMetrics?: ApiTableGrouping[];
}

function ExplorerTable({
  onClick,
  metrics,
  limit,
  colWidths,
  extraDimensions,
  dontIncludeEmpty,
  tableCustomHeaderRender,
  ignoreAttrModel,

  groupedMetrics: groupedMetricsProp,
}: IExplorerTableProps) {
  const { dimension, selectedRanks, having, attributes, config } =
    useExplorerContext();
  const groupMetricsByCategory = useGroupMetricsByCategory();

  const groupedMetrics = useMemo(
    () => groupedMetricsProp || groupMetricsByCategory(metrics),
    [metrics, groupMetricsByCategory, groupedMetricsProp],
  );

  const displayOptions = useMemo(() => {
    const o: ApiChartDisplayOptions = {
      noFilters: true,
      noTitle: true,
      noAttributionToggle: true,
      showTotals: true,
      rowsPerPage: 0,
      tableGroupings: groupedMetrics,
    };
    if (colWidths) {
      o.tableColWidths = colWidths;
    }
    return o;
  }, [colWidths, groupedMetrics]);

  return (
    <AnalyticsCard
      ignoreAttrModel={ignoreAttrModel}
      noToolbar
      noTopBar
      tableCustomHeaderRender={tableCustomHeaderRender}
      chart={{
        name: "",
        dimension: extraDimensions
          ? [dimension, ...extraDimensions]
          : [dimension],
        template: ApiChartTemplate.TABLE,
        type: ApiChartType.TABLE,
        attributes,
        ranks: selectedRanks,
        having: having,
        comparison: true,
        limit: limit,
        includeEmpty: !dontIncludeEmpty,
        orderBy: config.ordrerByDimension
          ? `${dimension}`
          : `${
              metrics.includes(ApiMetric.METRIC_RANK)
                ? ApiMetric.METRIC_RANK
                : metrics[0]
            } desc`,
        yAxisKeys: [
          {
            key: "l",
            fields: metrics,
          },
        ],
        displayOptions,
      }}
      onClick={onClick ? onClick : undefined}
    />
  );
}

export default ExplorerTable;
